import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Select, MenuItem, Stack, Divider } from "@mui/material";

import { SimpleFormLabel } from "components/dialogs/SimpleFormDialog";
import { QueryFieldsSelector } from "components/queries/queryBuilder/dialogParts/QueryFieldsSelector";
import TabbedDialog from "components/dialogs/TabbedDialog";
import SettingsOptions from "components/settingsConfig/SettingsOptions";
import SplitSettingsWrapper from "components/settingsConfig/SplitSettingsWrapper";
import SettingsAccordion from "components/settingsConfig/uiComponents/SettingsAccordion";
import SettingsWidgetTitleSelect from "components/settingsConfig/uiComponents/SettingsWidgetTitleSingleSelect";
import SettingsSingleSelect, { OptionsValueType } from "components/settingsConfig/uiComponents/SettingsSingleSelect";
import SettingsTextField from "components/settingsConfig/uiComponents/SettingsTextField";
import SettingsToggleButton, { horizontalAlignmentOptions } from "components/settingsConfig/uiComponents/SettingsToggleButton";
import SettingsSwitch from "components/settingsConfig/uiComponents/SettingsSwitch";
import { AutoWidgetQueryBehavior, getSingleOriginalRequestByName, WidgetRequest } from "system/widget/Widget";
import { WidgetSettingsProps } from "components/widgetParts/WidgetSettingsWrapper";
import { ClusteringBias, CLUSTERING_BIAS_OPTIONS, normalizeClusteringBias } from "components/widgets/utility/ClusteringBias";

export type ForceGraphSettingsStateType = {
  customWidgetTitle: string;
  widgetTitle: string;
  widgetTitleAlignment: string;
  displayLinks: boolean;
  colorBy: "cluster" | "sentiment";
  labelDisplay: 0 | 1 | 2;
};

export const defaultSettings: ForceGraphSettingsStateType = {
  customWidgetTitle: '',
  widgetTitle: 'none',
  widgetTitleAlignment: 'left',
  displayLinks: true,
  colorBy: "cluster",
  labelDisplay: 1
};

const defaultWidgetRequest = {
  name: 'force-graph',
  endpoint: 'query/cluster-graph',
  queryParams: { },
  requestInit: {
    body: {
      clusters: { size: 1000 }
    }
  },
  autoWidgetQueryBehavior: AutoWidgetQueryBehavior.AUTO_SINGLE
} as WidgetRequest;

export function BuildDefaultForceGraphRequests(): WidgetRequest[] | undefined  {
  const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
  const requestInitBody = {
    clusters: {
      size: 1000
    }
  };
  widgetRequest.requestInit = { body: requestInitBody };
  return [widgetRequest];
}

export default function ForceGraphSettingsContent(props: WidgetSettingsProps): JSX.Element {
  const { i18n } = useTranslation();

  const originalRequest = getSingleOriginalRequestByName(props.activeWidgetRequests, 'force-graph');
  const requestBody = originalRequest?.requestInit?.body || {};

  const [selectedFieldIds, setSelectedFieldIds] = useState<string[]>(requestBody?.clusters?.fields || []);
  const [clusteringBias, setClusteringBias] = useState<ClusteringBias>(normalizeClusteringBias(props.widget?.data?.clusteringBias));
  const [clusterSize, setClusterSize] = useState<number>(props.widget?.data?.clusterSize ?? 10000);

  const clusterSizeOptions = [
    {label: i18n.t('intlNumber', { val: 1000 }), value: 1000 },
    {label: i18n.t('intlNumber', { val: 2500 }), value: 2500 },
    {label: i18n.t('intlNumber', { val: 5000 }), value: 5000 },
    {label: i18n.t('intlNumber', { val: 10000 }), value: 10000 },
    {label: i18n.t('intlNumber', { val: 25000 }), value: 25000 },
    {label: i18n.t('intlNumber', { val: 50000 }), value: 50000 },
  ];

  const colorByOptions: Array<OptionsValueType> = [{
    label: i18n.t("cluster"),
    value: "cluster"
  }, {
    label: i18n.t("widget-types.force-graph.settings.color-by-options.sentiment"),
    value: "sentiment"
  }];

  const labelDisplayOptions: Array<OptionsValueType> = [{
    label: i18n.t("widget-types.force-graph.settings.label-display-options.none"),
    value: 0
  }, {
    label: i18n.t("widget-types.force-graph.settings.label-display-options.key-clusters"),
    value: 1
  }, {
    label: i18n.t("widget-types.force-graph.settings.label-display-options.all"),
    value: 2
  }];

  // To Add:
  // # Results/Size
  // # Fields
  // Query Term Exclusion Toggle
  // Exclude terms from clustering analysis

  const updateWidget = () => {
    const widgetRequest : WidgetRequest = { ...defaultWidgetRequest };
    const requestInitBody = {
      clusters: {
        size: clusterSize,
        fields: selectedFieldIds,
        clustering_bias: clusteringBias
      }
    };
    widgetRequest.requestInit = { body: requestInitBody };

    props.saveWidgetDataAndRequests({
      clusteringBias: clusteringBias,
      clusterSize: clusterSize,
      settings: settingsState
    }, [widgetRequest]).then(() => {
      if (props.widgetRefresh) {
        props.widgetRefresh();
      }
    })
  }


  const [tabValue, setTabValue] = useState<number>(0);
  const [settingsState, setSettingsState] = useState<ForceGraphSettingsStateType>({
    ...defaultSettings,
    ...props.widget?.data,
    ...props.widget?.data?.settings,
  });

  const settingsOnChange = (updatedState: ForceGraphSettingsStateType) => setSettingsState(updatedState);
  const handleTabChange = (event: React.SyntheticEvent<Element, Event>, newValue: number) => setTabValue(newValue);

  return (
    <TabbedDialog
      currentTab={tabValue}
      handleTabChange={handleTabChange}
      maxWidth="xs"
      onCancel={() => props.setSettingsDialogIsOpen(false)}
      onConfirm={() => {
        updateWidget();
        props.setSettingsDialogIsOpen(false);
      }}
      open={true}
      tabs={[i18n.t("widgets.settings.query-tab"), i18n.t("display")]}
      title={i18n.t("widget-types.force-graph.settings.title")}
    >
      <>
        {/* Tab #1 */}
        {tabValue === 0 && (<>
          {props.queryInfo && (<>
            <SimpleFormLabel>{i18n.t("field")}</SimpleFormLabel>
            <QueryFieldsSelector
              allowedFieldTypes={["text"]}
              selectedFieldIds={selectedFieldIds}
              setSelectedFieldIds={(newFieldIds: string[]) => {
                setSelectedFieldIds(newFieldIds);
              }}
              queryInfo={props.queryInfo}
              requireAggSupported={true}
            />

            <SimpleFormLabel>{i18n.t("widget-settings.clustering-bias.title")}</SimpleFormLabel>
            <Select
              value={clusteringBias}
              onChange={(event) => setClusteringBias(event.target.value as ClusteringBias)}
            >
              {CLUSTERING_BIAS_OPTIONS.map((option) => {
                return <MenuItem key={option.value} value={option.value}>{i18n.t(option.labelKey)}</MenuItem>
              })}
            </Select>
            <SimpleFormLabel>{i18n.t("widget-types.force-graph.settings.node-count")}</SimpleFormLabel>
            <Select
              value={clusterSize}
              onChange={(event) => setClusterSize(event.target.value as number)}
            >
              {clusterSizeOptions.map((key, index) => {
                return <MenuItem key={`cluster-size-option-${index}`} value={key.value}>{key.label}</MenuItem>
              })}
            </Select>
          </>)}
        </>)}
        {/* Tab #2 */}
        {tabValue === 1 &&
          <SplitSettingsWrapper>
            {/* Settings Options */}
            <SettingsOptions onChange={settingsOnChange} settingsState={settingsState}>
              <SettingsAccordion label={i18n.t("widget-settings.widget-title")}>
                <SettingsWidgetTitleSelect
                  keyid="widgetTitle"
                  label={i18n.t("title")}
                  showQueryOption={true}
                  widgetTitleValue={settingsState.widgetTitle} />
                {settingsState.widgetTitle === 'custom' &&
                  <Stack direction="row" spacing={2} sx={{ marginLeft: 2 }}>
                    <Divider flexItem orientation="vertical" sx={{ width: "5px" }} />
                    <SettingsTextField
                      keyid="customWidgetTitle"
                      label={i18n.t("widget-settings.custom-text")}
                      value={settingsState.customWidgetTitle}
                    />
                  </Stack>}
                <SettingsToggleButton
                  keyid="widgetTitleAlignment"
                  label={i18n.t("widget-settings.alignment")}
                  optionsList={horizontalAlignmentOptions}
                  value={settingsState.widgetTitleAlignment} />
              </SettingsAccordion>
              <SettingsSwitch keyid="displayLinks" label={i18n.t("widget-types.force-graph.settings.display-links")} value={settingsState.displayLinks}/>
              <SettingsSingleSelect
                defaultValue={colorByOptions.find((option) => option.value === defaultSettings.colorBy) || colorByOptions[0]}
                keyid="colorBy"
                label={i18n.t("widget-types.force-graph.settings.color-by")}
                options={colorByOptions}
                value={colorByOptions.find((option) => option.value === settingsState.colorBy)}
              />
              <SettingsSingleSelect
                defaultValue={labelDisplayOptions.find((option) => option.value === defaultSettings.labelDisplay) || labelDisplayOptions[0]}
                keyid="labelDisplay"
                label={i18n.t("widget-types.force-graph.settings.cluster-labels")}
                options={labelDisplayOptions}
                value={labelDisplayOptions.find((option) => option.value === settingsState.labelDisplay)}
              />
            </SettingsOptions>
          </SplitSettingsWrapper>
        }
      </>

    </TabbedDialog>
  );
}
